// SPDX-License-Identifier: AGPL-3.0-or-later
import {CommandBlock} from '@app/features/konvo_agents/components/AgentBits';
import {ProviderLogo} from '@app/features/konvo_agents/components/AgentBrand';
import {CONNECTIONS_TITLE} from '@app/features/konvo_agents/components/AgentConnectionCopy';
import {openAgentConnections} from '@app/features/konvo_agents/components/AgentConnections';
import {openAgentProjectDialog} from '@app/features/konvo_agents/components/AgentProjectPicker';
import styles from '@app/features/konvo_agents/components/AgentSetup.module.css';
import {openAgentsReconnectModal} from '@app/features/konvo_agents/components/AgentsConnectModal';
import {agentProblemMessage} from '@app/features/konvo_agents/components/AgentsCopy';
import {
	type SetupPlatform,
	type SetupProvider,
	setupAgentName,
	setupDocs,
	setupFindCommand,
	setupInstallCommand,
	setupLoginCommand,
	setupStatus,
	setupStatusCommand,
} from '@app/features/konvo_agents/state/AgentSetupModel';
import Agents from '@app/features/konvo_agents/state/Agents';
import Navigation from '@app/features/navigation/state/Navigation';
import type {AgentMachineView} from '@konvo/schema/src/domains/konvo/Agents';
import {Trans, useLingui} from '@lingui/react/macro';
import {
	IconArrowRight,
	IconCheck,
	IconChevronRight,
	IconDeviceLaptop,
	IconInfoCircle,
	IconRefresh,
} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react-lite';
import {type ReactNode, useEffect, useRef, useState} from 'react';

export function needsAgentSetup(machine: AgentMachineView): boolean {
	if (machine.toolProblem) return false;
	return (
		Agents.setupOpen.has(machine.id) ||
		(!machine.tools.some((tool) => tool.models?.length) && machine.tools.some((tool) => tool.agents?.length))
	);
}

function Step({number, title, children}: {number: number; title: ReactNode; children: ReactNode}) {
	return (
		<div className={styles.step}>
			<span className={styles.number}>{number}</span>
			<div className={styles.stepBody}>
				<h3>{title}</h3>
				{children}
			</div>
		</div>
	);
}

export const AgentSetup = observer(
	({
		machine,
		compact = false,
		continueHere = false,
	}: {
		machine: AgentMachineView;
		compact?: boolean;
		continueHere?: boolean;
	}) => {
		const {t, i18n} = useLingui();
		const [provider, setProvider] = useState<SetupProvider>(() =>
			setupStatus(machine, 'codex') === 'ready' || setupStatus(machine, 'codex') === 'sign_in'
				? 'codex'
				: 'claudeAgent',
		);
		const [chosenPlatform, setChosenPlatform] = useState<SetupPlatform>('other');
		const [finding, setFinding] = useState(false);
		const [path, setPath] = useState('');
		const [busy, setBusy] = useState(false);
		const [failure, setFailure] = useState<string | null>(null);
		const serial = useRef(0);
		const machineId = machine.id;
		useEffect(() => {
			if (!compact && !machine.tools.some((tool) => tool.models?.length))
				runInAction(() => Agents.setupOpen.add(machineId));
			return () => {
				serial.current++;
				if (!compact) runInAction(() => Agents.setupOpen.delete(machineId));
			};
		}, [machineId, compact]);
		useEffect(() => {
			serial.current++;
			setBusy(false);
			setFailure(null);
			setFinding(false);
			setPath('');
		}, [machineId, provider]);
		const status = setupStatus(machine, provider);
		const platform = machine.platform === 'other' ? chosenPlatform : machine.platform;
		const name = setupAgentName(provider);
		const machineName = machine.name;
		const osName =
			platform === 'win32'
				? 'Windows'
				: platform === 'darwin'
					? 'macOS'
					: platform === 'linux'
						? 'Linux'
						: t`Unknown operating system`;
		const shell = platform === 'win32' ? 'PowerShell' : t`Terminal`;
		const canCheck = machine.tools.some((tool) => tool.can.setup);
		const providerReady = status === 'ready';
		const stage = busy
			? 'checking'
			: finding
				? 'find'
				: failure || status === 'unavailable'
					? 'error'
					: providerReady
						? 'ready'
						: status === 'sign_in'
							? 'signin'
							: status === 'checking'
								? 'checking'
								: status === 'disabled'
									? 'disabled'
									: 'install';
		const ready = stage === 'ready';
		const installCommand = setupInstallCommand(provider, platform);
		const findCommand = setupFindCommand(provider, platform);
		const check = async (binaryPath?: string) => {
			const id = ++serial.current;
			setBusy(true);
			setFailure(null);
			try {
				if (canCheck)
					await Agents.checkAgentSetup(machine.id, {provider, ...(binaryPath ? {binaryPath: binaryPath.trim()} : {})});
				else await Agents.loadOverview();
				if (serial.current === id) setFinding(false);
			} catch (error) {
				if (serial.current === id) setFailure(agentProblemMessage(i18n, error));
			} finally {
				if (serial.current === id) setBusy(false);
			}
		};
		const start = () => {
			runInAction(() => Agents.setupOpen.delete(machine.id));
			if (continueHere) return;
			if (!machine.limits.send) {
				Navigation.navigateToAgents(machine.id);
				return;
			}
			const project = Agents.projectsOf(machine.id)[0] ?? machine.projects[0];
			if (project) Navigation.navigateToNewAgentChat(machine.id, project.id);
			else
				openAgentProjectDialog(null, (choice) => Navigation.navigateToNewAgentChat(choice.machineId, choice.projectId));
		};
		const progress = stage === 'ready' ? 2 : stage === 'signin' || stage === 'checking' ? 1 : 0;
		const projects = Agents.projectsOf(machine.id).length ? Agents.projectsOf(machine.id) : machine.projects;
		const project = projects[0];
		const code = (command: string, label?: string) => (
			<div className={styles.codeGroup}>
				{label && <div className={styles.codeLabel}>{label}</div>}
				<CommandBlock lines={[command]} wrap prompt={false} className={styles.code} />
			</div>
		);
		const again = (
			<button
				type="button"
				className={clsx(styles.button, styles.primary)}
				onClick={() => void check()}
				disabled={busy || !machine.online}
				data-flx="konvo-agents.setup.check"
			>
				<IconRefresh size={14} />
				<Trans>Check again</Trans>
			</button>
		);
		return (
			<section className={clsx(styles.setup, compact && styles.compact)} data-flx="konvo-agents.setup">
				{machine.tools.some((tool) => tool.connections?.length) && (
					<button type="button" className={styles.button} onClick={() => openAgentConnections(machine.id)}>
						{i18n._(CONNECTIONS_TITLE)}
					</button>
				)}
				{!compact && (
					<>
						<div className={styles.machine}>
							<IconDeviceLaptop size={16} />
							<strong>{machineName}</strong>
							<span>·</span>
							<span>{osName}</span>
							<span className={styles.connected}>
								<i />
								{machine.online ? <Trans>Connected</Trans> : <Trans>Offline</Trans>}
							</span>
						</div>
						<h1>
							{stage === 'ready' ? <Trans>Your agent is ready</Trans> : <Trans>Finish setting up your agents</Trans>}
						</h1>
						<p className={styles.lead}>
							{stage === 'ready' ? (
								<Trans>Your computer and coding agent are connected to Konvo.</Trans>
							) : (
								<Trans>Choose a coding agent to use on {machineName}. You only need one.</Trans>
							)}
						</p>
						<div className={styles.progress} role="group" aria-label={t`Setup progress`}>
							{[t`Install CLI`, t`Sign in`, t`Ready`].map((label, index) => (
								<div key={label} className={clsx(index === progress && styles.active, index < progress && styles.done)}>
									<span>
										<i>{index < progress ? <IconCheck size={13} /> : index + 1}</i>
										{label}
									</span>
									{index < 2 && <b />}
								</div>
							))}
						</div>
					</>
				)}
				<div className={styles.agentPicker}>
					{(['claudeAgent', 'codex'] as const).map((id) => (
						<button
							type="button"
							key={id}
							className={clsx(styles.agent, provider === id && styles.selected)}
							onClick={() => setProvider(id)}
							aria-pressed={provider === id}
							data-flx={`konvo-agents.setup.${id}`}
						>
							<ProviderLogo provider={id} size={23} />
							<span>
								<strong>{setupAgentName(id)} CLI</strong>
								<small>{id === 'codex' ? 'OpenAI' : 'Anthropic'}</small>
							</span>
							<i className={styles.radio}>{provider === id && <b />}</i>
						</button>
					))}
				</div>
				{machine.platform === 'other' && (
					<label className={styles.platform}>
						<Trans>Operating system on {machineName}</Trans>
						<select value={chosenPlatform} onChange={(event) => setChosenPlatform(event.target.value as SetupPlatform)}>
							<option value="other">{t`Choose operating system`}</option>
							<option value="win32">Windows</option>
							<option value="darwin">macOS</option>
							<option value="linux">Linux</option>
						</select>
					</label>
				)}
				<section className={styles.card} aria-live="polite" aria-busy={busy}>
					<div className={styles.cardHead}>
						<div>
							<ProviderLogo provider={provider} size={20} />
							<h2>
								{stage === 'install' ? (
									<Trans>Install {name} CLI</Trans>
								) : stage === 'find' ? (
									<Trans>Find your existing CLI</Trans>
								) : stage === 'signin' ? (
									<Trans>Sign in to {name}</Trans>
								) : stage === 'ready' ? (
									<Trans>{name} is ready</Trans>
								) : stage === 'checking' ? (
									<Trans>Checking {name}</Trans>
								) : stage === 'disabled' ? (
									<Trans>Enable {name}</Trans>
								) : (
									<Trans>We couldn't check {name}</Trans>
								)}
							</h2>
						</div>
						<span className={clsx(styles.badge, ready && styles.badgeReady)}>
							{ready ? (
								<>
									<IconCheck size={12} />
									<Trans>Ready</Trans>
								</>
							) : stage === 'install' ? (
								<Trans>Not detected</Trans>
							) : stage === 'signin' ? (
								<Trans>Sign-in needed</Trans>
							) : stage === 'checking' ? (
								<Trans>Checking…</Trans>
							) : stage === 'find' ? (
								name
							) : (
								<Trans>Needs attention</Trans>
							)}
						</span>
					</div>
					{stage === 'install' && (
						<>
							<p className={styles.cardIntro}>
								<Trans>
									Install the command-line app on {machineName}. A desktop app or editor extension on its own may not
									provide the CLI.
								</Trans>
							</p>
							{installCommand && (
								<Step
									number={1}
									title={
										<Trans>
											Open {shell} on {machineName}
										</Trans>
									}
								>
									<p>
										<Trans>Copy and run the install command below.</Trans>
									</p>
									{code(installCommand, `${osName} · ${shell}`)}
									{provider === 'codex' && (
										<p className={styles.small}>
											<Trans>This uses npm, included with the Node.js installation for your Konvo connection.</Trans>
										</p>
									)}
								</Step>
							)}
							<div className={styles.next}>
								<span>
									<Trans>Next</Trans>
								</span>
								<Trans>Sign in to your {name} account</Trans>
							</div>
							<div className={styles.cardActions}>
								{again}
								<button
									type="button"
									className={styles.button}
									onClick={() => setFinding(true)}
									data-flx="konvo-agents.setup.already-installed"
								>
									<Trans>Already installed?</Trans>
								</button>
							</div>
						</>
					)}
					{stage === 'find' && (
						<>
							<p className={styles.cardIntro}>
								<Trans>
									We couldn't find {name} CLI from Konvo's background connection. Help us find the installation you
									already use.
								</Trans>
							</p>
							{findCommand && (
								<Step number={1} title={<Trans>Find its path on {machineName}</Trans>}>
									<p>
										<Trans>In the terminal where {name} works, run:</Trans>
									</p>
									{code(findCommand, shell)}
								</Step>
							)}
							{canCheck && machine.limits.send ? (
								<Step number={2} title={<Trans>Use the path it prints</Trans>}>
									<form
										onSubmit={(event) => {
											event.preventDefault();
											if (path.trim()) void check(path);
										}}
									>
										{failure && (
											<p className={styles.pathError} role="alert">
												{failure}
											</p>
										)}
										<label className={styles.inputLabel} htmlFor={`agent-path-${machine.id}`}>
											<Trans>CLI path on {machineName}</Trans>
										</label>
										<input
											id={`agent-path-${machine.id}`}
											className={styles.input}
											value={path}
											onChange={(event) => setPath(event.target.value)}
											maxLength={2000}
											spellCheck={false}
											placeholder={
												platform === 'win32'
													? `C:\\Users\\YourName\\.local\\bin\\${provider === 'codex' ? 'codex' : 'claude'}.exe`
													: `/home/yourname/.local/bin/${provider === 'codex' ? 'codex' : 'claude'}`
											}
											required
										/>
										<p className={styles.small}>
											<Trans>Konvo will check the CLI and its sign-in before using it.</Trans>
										</p>
										<button
											type="submit"
											className={clsx(styles.button, styles.primary)}
											disabled={!path.trim() || busy || !machine.online}
											data-flx="konvo-agents.setup.path-check"
										>
											<Trans>Check this path</Trans>
										</button>
									</form>
								</Step>
							) : (
								<p className={styles.cardIntro}>
									{!canCheck ? (
										<>
											<Trans>Update this machine's Konvo connection to check a CLI path here.</Trans>
											<button type="button" className={styles.button} onClick={() => openAgentsReconnectModal(machine)}>
												<Trans>Repair connection</Trans>
											</button>
										</>
									) : (
										<Trans>This machine allows reading only. Change its agent path in T3 Code on that computer.</Trans>
									)}
								</p>
							)}
							<div className={styles.inlineNote}>
								<IconInfoCircle size={15} />
								<p>
									{platform === 'win32' ? (
										<Trans>
											Using WSL? Run the Konvo connection command inside the same WSL environment as your agent.
										</Trans>
									) : (
										<Trans>Use the same user account and environment where your agent runs.</Trans>
									)}
								</p>
							</div>
							<div className={styles.cardActions}>
								<button type="button" className={styles.button} onClick={() => setFinding(false)}>
									<Trans>Back to install</Trans>
								</button>
							</div>
						</>
					)}
					{stage === 'signin' && (
						<>
							<div className={styles.successLine}>
								<IconCheck size={15} />
								<Trans>
									{name} CLI found on {machineName}
								</Trans>
							</div>
							<p className={styles.cardIntro}>
								<Trans>One last step: sign in on {machineName} so Konvo can use your account.</Trans>
							</p>
							<Step number={1} title={<Trans>Run this in {shell}</Trans>}>
								{code(setupLoginCommand(provider, path, platform), `${osName} · ${shell}`)}
							</Step>
							<Step number={2} title={<Trans>Complete sign-in in your browser</Trans>}>
								<p>
									<Trans>
										Use the account you want this computer to run your chats with. Konvo will detect it automatically.
									</Trans>
								</p>
							</Step>
							<div className={styles.cardActions}>{again}</div>
						</>
					)}
					{stage === 'checking' && (
						<>
							<p className={styles.cardIntro}>
								<Trans>Checking the agent on {machineName}. This usually takes a few seconds.</Trans>
							</p>
							<div className={styles.checklist}>
								<span className={styles.spinner} />
								<span>
									<Trans>Checking the CLI, sign-in and available models</Trans>
								</span>
							</div>
							{!busy && <div className={styles.cardActions}>{again}</div>}
						</>
					)}
					{stage === 'error' && (
						<>
							<p className={styles.cardIntro}>
								{failure ?? t`The agent check didn't finish. Your existing installation is still there.`}
							</p>
							<p className={styles.cardIntro}>
								<Trans>
									Open {shell} on {machineName}, check the agent's sign-in, then try again.
								</Trans>
							</p>
							{code(setupStatusCommand(provider, path, platform), `${osName} · ${shell}`)}
							<div className={styles.cardActions}>
								{again}
								<button
									type="button"
									className={styles.button}
									onClick={() => {
										setFailure(null);
										setFinding(true);
									}}
								>
									<Trans>Use another path</Trans>
								</button>
							</div>
						</>
					)}
					{stage === 'disabled' && (
						<>
							<p className={styles.cardIntro}>
								<Trans>
									Open T3 Code on {machineName} and enable {name}. Konvo automatically enables an existing CLI after
									confirming its sign-in.
								</Trans>
							</p>
							<div className={styles.cardActions}>
								{again}
								<button type="button" className={styles.button} onClick={() => setFinding(true)}>
									<Trans>Already installed?</Trans>
								</button>
							</div>
						</>
					)}
					{stage === 'ready' && (
						<>
							<p className={styles.cardIntro}>
								<Trans>
									You're signed in on {machineName}. Start a chat in one of this computer's shared projects.
								</Trans>
							</p>
							{project && (
								<div className={styles.project}>
									<span className={styles.folder}>
										<IconDeviceLaptop size={18} />
									</span>
									<div>
										<strong>{project.title}</strong>
										<span>
											<Trans>Current checkout</Trans>
										</span>
									</div>
									<IconChevronRight size={15} />
								</div>
							)}
							<div className={styles.successLine}>
								<IconCheck size={15} />
								<Trans>Installed · signed in · models available</Trans>
							</div>
							<div className={styles.cardActions}>
								<button
									type="button"
									className={clsx(styles.button, styles.primary)}
									onClick={start}
									data-flx="konvo-agents.setup.start"
								>
									{!machine.limits.send ? (
										<Trans>View chats</Trans>
									) : project ? (
										<Trans>Start a chat</Trans>
									) : (
										<Trans>Choose a project</Trans>
									)}
									<IconArrowRight size={14} />
								</button>
							</div>
						</>
					)}
				</section>
				<div className={styles.footer}>
					{ready ? (
						<Trans>Konvo reconnects automatically while {machineName} is online.</Trans>
					) : (
						<>
							<Trans>Already signed in? Konvo checks automatically within a minute.</Trans>
							<a href={setupDocs(provider)} target="_blank" rel="noreferrer">
								<Trans>Setup help</Trans>
								<IconChevronRight size={11} />
							</a>
						</>
					)}
				</div>
			</section>
		);
	},
);
